import {
  Component,
  ChangeDetectionStrategy,
  input,
  output,
  signal,
  computed,
  effect,
  forwardRef,
  model,
  TemplateRef,
  ContentChild,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  Validator,
  AbstractControl,
  ValidationErrors,
  FormsModule,
} from '@angular/forms';
import { AutoCompleteModule, AutoCompleteCompleteEvent, AutoCompleteSelectEvent, AutoComplete } from 'primeng/autocomplete';
import { TranslateLangPipe, IconComponent } from '@gs-2.0/shared';
import { NgTemplateOutlet } from '@angular/common';

const AUTOCOMPLETE_VALUE_ACCESSOR = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => AutocompleteFieldComponent),
  multi: true,
};

const AUTOCOMPLETE_VALIDATOR = {
  provide: NG_VALIDATORS,
  useExisting: forwardRef(() => AutocompleteFieldComponent),
  multi: true,
};

@Component({
  selector: 'app-autocomplete-field',
  standalone: true,
  imports: [AutoCompleteModule, FormsModule, TranslateLangPipe, IconComponent, NgTemplateOutlet],
  providers: [AUTOCOMPLETE_VALUE_ACCESSOR, AUTOCOMPLETE_VALIDATOR],
  templateUrl: './autocomplete-field.component.html',
  styleUrl: './autocomplete-field.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AutocompleteFieldComponent<T = any> implements ControlValueAccessor, Validator {
  // Content children
  @ContentChild('itemTemplate') itemTemplate: TemplateRef<any> | null = null;
  @ViewChild(AutoComplete) autoComplete!: AutoComplete;

  // Inputs
  label = input<string>('');
  placeholder = input<string>('');
  errorMessage = input<string>('');
  required = input<boolean>(false);
  hideRequiredInfo = input<boolean>(false);
  disabled = input<boolean>(false);
  suggestions = input<T[]>([]);
  optionLabel = input<string>('name');
  showClear = input<boolean>(true);
  showSearchIcon = input<boolean>(false);
  showCaretIcon = input<boolean>(false);
  completeOnFocus = input<boolean>(true);
  forceSelection = input<boolean>(true);
  ariaLabel = input<string>('Autocomplete');
  validationDelay = input<number>(300);
  helperText = input<string | null>(null);

  // Outputs
  search = output<string>();
  optionSelect = output<T>();
  blur = output<void>();
  clear = output<void>();
  hasNoValue = output<boolean>();

  // Internal state
  value = model<T | null>(null);
  isTouched = signal(false);
  showDelayedError = signal(false);
  isDropdownOpen = signal(false);
  private validationTimer: any = null;

  // Computed
  isInvalid = computed(() =>
    this.required() && !this.value() && this.isTouched() && this.showDelayedError()
  );

  // ControlValueAccessor callbacks
  private onChange: (value: T | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    // Sync value changes to form control
    effect(() => {
      const currentValue = this.value();
      queueMicrotask(() => {
        this.onChange(currentValue);
      });
    });
  }

  // ControlValueAccessor implementation
  writeValue(value: T | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: T | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // this.disabled.set(isDisabled);
  }

  // Validator implementation
  validate(control: AbstractControl): ValidationErrors | null {
    if (this.required() && !control.value) {
      return { required: true };
    }
    return null;
  }

  // Event handlers
  onBlur(): void {
    this.isTouched.set(true);
    this.onTouched();
    this.blur.emit();

    // Start delayed validation
    if (this.validationTimer) {
      clearTimeout(this.validationTimer);
    }
    this.validationTimer = setTimeout(() => {
      this.showDelayedError.set(true);
      if (this.required() && !this.value()) {
        this.hasNoValue.emit(true);
      }
    }, this.validationDelay());
  }

  onSearch(event: AutoCompleteCompleteEvent): void {
    this.search.emit(event.query);
  }

  onSelect(event: AutoCompleteSelectEvent): void {
    this.value.set(event.value);
    this.showDelayedError.set(false);
    this.optionSelect.emit(event.value);
    this.hasNoValue.emit(false);
  }

  onClear(): void {
    this.value.set(null);
    this.clear.emit();
    this.hasNoValue.emit(true);
  }

  onCaretClick(): void {
    this.autoComplete.inputEL?.nativeElement?.focus();
    this.autoComplete.show();
  }

  onDropdownShow(): void {
    this.isDropdownOpen.set(true);
  }

  onDropdownHide(): void {
    this.isDropdownOpen.set(false);
  }
}
